import React from 'react';
import { ShareButtons } from './ShareButtons';

/**
 * Small DRY wrapper around ShareButtons for the "share row" that lives at
 * the top of every primary page. Lives in a flex container so it can be
 * paired with a country-rules pill on the left (used on /will and
 * /inheritance) and an OPTIONAL localized "country + language locked"
 * share row on the right (used on the homepage).
 *
 * Props:
 *   - testid    : data-testid prefix passed through to ShareButtons
 *   - title     : OS-level share-sheet title (used by `navigator.share`)
 *   - text      : OS-level share-sheet body
 *   - leftSlot  : optional React node rendered on the left side of the row
 *   - rightSlot : optional React node rendered AFTER the ShareButtons row
 *                 (e.g. the LocalizedShareRow with the country+language QR)
 *   - align     : 'between' (default) | 'end' — controls justify-content
 */
export const PageHeaderShareRow = ({
  testid,
  title,
  text,
  leftSlot = null,
  rightSlot = null,
  align = 'between',
  compact = false,
}) => (
  <div
    className={`flex items-center -mt-2 mb-2 gap-2 flex-wrap ${
      align === 'between' ? 'justify-between' : 'justify-end'
    }`}
  >
    {leftSlot}
    {/* Mobile: single scrollable row so the share icons never wrap into
        multiple stacked lines. Desktop keeps the wrapping behaviour so
        the row can flow next to a leftSlot. Extra hidden padding-right
        gives thumb-friendly scroll room on very small screens. */}
    <div
      className="flex items-center gap-1.5 flex-nowrap overflow-x-auto no-scrollbar sm:flex-wrap sm:overflow-visible max-w-full pr-1"
      data-testid={testid ? `${testid}-row` : undefined}
    >
      <ShareButtons
        testid={testid}
        title={title}
        text={text}
        compact={compact}
        // When a rightSlot (LocalizedShareRow) is present it already
        // surfaces its own Poster + QR affordances, so ShareButtons should
        // skip those to prevent duplication (and the vertical wrap that
        // caused).
        hideExtras={!!rightSlot}
      />
      {rightSlot}
    </div>
  </div>
);

export default PageHeaderShareRow;
